/* Seiten der Startansicht ("Swipe-Seiten") — reine Funktionen, kein DOM, keine Texte.
 *
 * Die Startansicht besteht aus mehreren Seiten, zwischen denen man wischt: heute "Grassrunners FC"
 * (Kader) und "AFH Transfers". Jede Seite ist ein Eintrag in SQUAD_PAGES, von links nach
 * rechts. Eine weitere Seite (geplant: "AFH Partners" zwischen Kader und Transferred) ist nur ein
 * weiterer Eintrag hier plus ein Eintrag in PAGE_VIEWS (components/SquadPager.vue) und die Texte.
 *
 * logo/titleKey: Kopf der Seite. Ohne beides zeigt der Kopf das Wappen und den Namen des Vereins
 * (domain/clubs.js). logoSmall = kleine Fassung (256 px) für die schmale Leiste (PageBar.vue); fehlt sie,
 * gilt logo. logoScale = Kantenlänge des Logos im Logo-Kasten der Umlaufbahn (Anteil). Die
 * Beschriftung (Punkte, Logo-Knöpfe) kommt aus tabKey (Sprachdateien).
 */

import { POSITION_GROUPS, positionGroup, groupByLetter, defenseRank } from './positions.js';
import { groupByAge, DEFAULT_AGE_DIRECTION } from './squadFilter.js';

export const SQUAD_PAGES = [
  { id: 'squad', tabKey: 'squadPages.squad', logo: null, logoScale: 0.78, titleKey: null },
  {
    id: 'transferred',
    tabKey: 'squadPages.transferred',
    logo: 'afh-transferred-logo-512.png',
    logoSmall: 'afh-transferred-logo-256.png',
    logoScale: 1,
    titleKey: 'squadPages.transferredTitle',
  },
];

/* ---------- Seitenposition ----------
 *
 * Die Startansicht kennt eine gleitende Position `pos` (Kommazahl, 0 = erste Seite). Ganze Zahlen sind
 * Ruhezustände, dazwischen läuft die Bewegung (Wischen, Animation). Kopf (Logo-Umlaufbahn) und Listen
 * werden aus derselben `pos` berechnet.
 *
 * Bis zu 2 Seiten gibt es Anschläge an den Enden; ab ENDLESS_FROM Seiten läuft die Bahn endlos in beide
 * Richtungen (kein Anschlag, Pfeile nie gesperrt, kürzester Weg). */

/** Ab dieser Seitenzahl dreht sich die Bahn endlos. */
export const ENDLESS_FROM = 3;

export const isEndless = (n) => n >= ENDLESS_FROM;

/** Ganzzahliger Rest, der auch bei negativen Zahlen im Bereich [0, n) liegt. */
export function mod(x, n) {
  return ((x % n) + n) % n;
}

/**
 * Abstand d einer Seite zur Position, bei Endlos auf [-n/2, n/2) umgebrochen (kürzester Weg);
 * sonst unverändert.
 */
export function wrapOffset(d, n) {
  if (!isEndless(n)) return d;
  return mod(d + n / 2, n) - n / 2;
}

/** Position in den gültigen Bereich: Endlos modulo n, sonst auf [0, n-1] begrenzt. */
export function normalizePos(pos, n) {
  if (n <= 1) return 0;
  return isEndless(n) ? mod(pos, n) : Math.min(Math.max(pos, 0), n - 1);
}

/** Welche Seite ist die aktive (nächste ganze Seite)? */
export function activeIndexOf(pos, n) {
  if (n <= 1) return 0;
  return normalizePos(Math.round(pos), n);
}

/** Darf man von `index` aus einen Schritt in Richtung `delta` (-1/+1) gehen? Endlos immer. */
export function canStep(index, delta, n) {
  if (n <= 1) return false;
  if (isEndless(n)) return true;
  const next = index + delta;
  return next >= 0 && next <= n - 1;
}

/** Ziel (ungewrappt, ganze Zahl) für einen Schritt ab der aktuellen Position; ohne Endlos begrenzt. */
export function stepTarget(pos, delta, n) {
  const target = Math.round(pos) + delta;
  return isEndless(n) ? target : Math.min(Math.max(target, 0), Math.max(n - 1, 0));
}

/** Schritte (mit Vorzeichen) von Seite `from` zu Seite `to`: bei Endlos der kürzeste Weg. */
export function deltaBetween(from, to, n) {
  return wrapOffset(to - from, n);
}

/* ---------- Aktive Seite merken (URL-Hash) ----------
 *
 * Die aktive Seite steht als Seiten-ID im Hash (#transferred). Die erste Seite hat keinen Hash (saubere URL).
 * Der Hash dient auch Einladungs-/Passwort-Links von Supabase (#access_token=…, type=recovery): die sind
 * keine Seiten-ID und fallen hier auf die erste Seite zurück; sie werden nie überschrieben, solange
 * nur die erste Seite aktiv ist (siehe hashForPage / SquadPager.vue). */

/** Index der Seite zu einem Hash ('#transferred'); unbekannt, leer oder fremd → 0 (erste Seite). */
export function pageIndexFromHash(hash, pages) {
  const id = typeof hash === 'string' ? hash.replace(/^#/, '') : '';
  const i = (pages || []).findIndex((p) => p.id === id);
  return i < 0 ? 0 : i;
}

/** Hash für die Seite mit diesem Index: '' für die erste Seite, sonst '#<id>'. */
export function hashForPage(index, pages) {
  return index > 0 && pages?.[index] ? `#${pages[index].id}` : '';
}

/** Ist dieser Hash die ID einer Seite? (Nur dann darf er beim Zurück zur ersten Seite entfernt werden.) */
export function isPageHash(hash, pages) {
  const id = typeof hash === 'string' ? hash.replace(/^#/, '') : '';
  return !!id && (pages || []).some((p) => p.id === id);
}

/* ---------- Logo-Umlaufbahn (Kopf) ---------- */

/** Seitengröße der Logos in px (Kasten) und Abmessungen der Bühne, wie mit Bastian abgestimmt. */
export const ORBIT = {
  logoSize: 128,
  stageHeight: 230,
  rxRatio: 0.3, // Halbachse in Anteilen der Bühnenbreite
  cyRatio: 0.62, // Mittelhöhe in Anteilen der Bühnenhöhe
  ry: 34,
  depthFlatten: 0.7,
  lift: 10,
  dimBelowZ: 0.2,
  dimBrightness: 0.85,
};

/** Winkelschritt zwischen zwei Nachbarseiten auf der Bahn (rad). */
export function orbitStep(n) {
  return (2 * Math.PI) / (n === 2 ? 2.6 : n * 1.15);
}

/**
 * Platzierung eines Logos. d = Seitenindex - pos (bei Endlos schon umgebrochen, siehe wrapOffset).
 * x, y sind die Mitte des Logos in der Bühne (y wächst nach unten: z = 1 ist vorn, also tiefer).
 * @returns {{x:number,y:number,scale:number,opacity:number,zIndex:number,dim:boolean,front:number}}
 *          front = Nähe zur Vorderseite 0..1 (1 = ganz vorn)
 */
export function orbitPlacement(d, { n, stageWidth, stageHeight = ORBIT.stageHeight }) {
  const a = d * orbitStep(n);
  const z = Math.cos(a);
  const front = (z + 1) / 2;
  return {
    x: stageWidth / 2 + ORBIT.rxRatio * stageWidth * Math.sin(a),
    y: stageHeight * ORBIT.cyRatio + ORBIT.ry * z * ORBIT.depthFlatten - ORBIT.lift,
    scale: 0.45 + 0.55 * front,
    opacity: 0.45 + 0.55 * front,
    zIndex: Math.round((z + 1) * 50),
    dim: z < ORBIT.dimBelowZ,
    front,
  };
}

/** Deckkraft des Titels einer Seite beim Drehen (weiches Überblenden). */
export function titleOpacity(d) {
  return Math.max(0, 1 - Math.abs(d) * 1.4);
}

/** Beim Listenbereich: ab dieser Entfernung (in Seitenbreiten) ist eine Seite unsichtbar. */
export const LIST_VISIBLE_BELOW = 1.01;

/* ---------- Wischen ---------- */

/** Erst ab so vielen Pixeln Bewegung wird entschieden, ob waagerecht gewischt oder senkrecht gescrollt wird. */
export const INTENT_MIN_PX = 8;
/** Waagerecht gilt nur, wenn die waagerechte Bewegung deutlich größer ist als die senkrechte. */
export const INTENT_RATIO = 1.5;
/** Schneller kurzer Wisch ("Flick"): ab dieser Geschwindigkeit (px/ms) ... */
export const FLICK_SPEED = 0.4;
/** ... und mindestens dieser Strecke (px) geht er eine Stufe weiter in Wischrichtung. */
export const FLICK_MIN_PX = 30;
/** Ohne Endlos folgt die Position über die Enden hinaus nur zu diesem Anteil. */
export const EDGE_RESISTANCE = 0.3;
/** Stufenloses Wischen: eine Seite entspricht so viel Fensterbreite. */
export const SWIPE_WIDTH_RATIO = 0.55;
/** Dauer der Animation beim Einrasten und bei Klick/Pfeil (ms). */
export const ANIMATION_MS = 450;

/**
 * Wohin geht die Geste? 'horizontal' (Seite wischen), 'vertical' (normales Scrollen, nicht anfassen)
 * oder null (noch zu wenig Bewegung). Die Entscheidung gilt für den Rest der Geste.
 */
export function swipeIntent(dx, dy) {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.max(ax, ay) < INTENT_MIN_PX) return null;
  return ax > ay * INTENT_RATIO ? 'horizontal' : 'vertical';
}

/**
 * Position während des Wischens, stufenlos: Startposition - dx / (Fensterbreite * 0.55).
 * Ohne Endlos wird über die Enden hinaus nur zu EDGE_RESISTANCE gefolgt.
 */
export function dragPosition({ startPos, dx, windowWidth, n }) {
  const raw = startPos - dx / (Math.max(windowWidth, 1) * SWIPE_WIDTH_RATIO);
  if (isEndless(n)) return raw;
  const last = Math.max(n - 1, 0);
  if (raw < 0) return raw * EDGE_RESISTANCE;
  if (raw > last) return last + (raw - last) * EDGE_RESISTANCE;
  return raw;
}

/**
 * Beim Loslassen: auf welche ganze Seite rastet die Bahn ein (ungewrappt; bei Endlos kann das Ziel
 * außerhalb [0, n) liegen, die Aufrufer normalisieren nach der Animation)?
 * Schneller Wisch (Flick) geht eine Stufe weiter in Wischrichtung, sonst die nächste ganze Seite.
 * dx = Strecke seit Beginn (negativ = nach links = nächste Seite), dt = Dauer in ms.
 */
export function settleTarget({ pos, startPos, dx, dt, n }) {
  const speed = dt > 0 ? Math.abs(dx) / dt : 0;
  const flick = speed >= FLICK_SPEED && Math.abs(dx) >= FLICK_MIN_PX;
  const target = flick ? Math.round(startPos) + (dx < 0 ? 1 : -1) : Math.round(pos);
  return isEndless(n) ? target : Math.min(Math.max(target, 0), Math.max(n - 1, 0));
}

/** Ease-out kubisch, t in [0, 1]. */
export function easeOutCubic(t) {
  const c = Math.min(Math.max(t, 0), 1);
  return 1 - Math.pow(1 - c, 3);
}

/* ---------- Transferred: Sortierung ---------- */

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const text = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

/**
 * Transferierte Spieler: Transferdatum, neueste zuerst; ohne Datum nach hinten; dann Nachname, Vorname.
 * dateOf(player) liefert 'JJJJ-MM-TT' oder ''. Verändert die Eingabe nicht.
 */
export function sortByTransferDate(players, dateOf) {
  return (players || []).slice().sort((a, b) => {
    const da = text(dateOf(a));
    const db = text(dateOf(b));
    if (da && !db) return -1;
    if (!da && db) return 1;
    if (da !== db) return db.localeCompare(da);
    return collator.compare(text(a.lastName), text(b.lastName)) || collator.compare(text(a.firstName), text(b.firstName));
  });
}

/** Sortierungen der Transfers-Seite; die ersten drei wie auf der Kaderseite, "date" ist eine flache Liste. */
export const TRANSFER_SORTS = ['position', 'az', 'age', 'date'];

/**
 * Transferierte Spieler für die Anzeige gruppieren (Form wie groupByPosition: key + members).
 *  position  Abschnitte je Positionsblock (Reihenfolge POSITION_GROUPS), darin neuester Transfer zuerst
 *  az        je Anfangsbuchstabe des Nachnamens (groupByLetter)
 *  age       je Jahrgang, jüngster zuerst bzw. umgekehrt (groupByAge)
 *  date      eine flache Liste, neuester Transfer zuerst (key '')
 * Ohne Datum steht ein Spieler im jeweiligen Abschnitt hinten (sortByTransferDate). Verändert die Eingabe nicht.
 */
export function transferSections(players, { sort = 'position', direction = DEFAULT_AGE_DIRECTION, dateOf = () => '' } = {}) {
  const list = players || [];
  if (sort === 'az') return groupByLetter(list);
  if (sort === 'age') return groupByAge(list, direction);
  if (sort === 'date') return list.length ? [{ key: '', members: sortByTransferDate(list, dateOf) }] : [];
  const sorted = sortByTransferDate(list, dateOf);
  return POSITION_GROUPS.map((key) => {
    const members = sorted.filter((p) => positionGroup(p.pos) === key);
    /* Defense nach Einzelposition (domain/positions.js DEFENSE_ORDER); die Sortierung ist stabil, innerhalb einer
       Position bleibt es also beim Transferdatum (neueste zuerst, ohne Datum hinten) */
    if (key === 'def') members.sort((a, b) => defenseRank(a.pos) - defenseRank(b.pos));
    return { key, members };
  }).filter((g) => g.members.length > 0);
}
